/**
 * Notification Service
 * Handles real-time notifications via Socket.io and REST API
 */

import { io } from 'socket.io-client';
import axios from 'axios';
import { getToken } from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class NotificationService {
    constructor() {
        this.socket = null;
        this.listeners = new Map();
        this.connected = false;
    }
    
    /**
     * Initialize Socket.io connection
     */
    connect(userId, userRole) {
        // Idempotent: once a socket exists, never create a second one. The
        // previous guard checked `this.socket && this.connected`, which let a
        // repeat call slip through during the (re)connection window and spawn a
        // duplicate socket — each adding another 'notification' handler that
        // multiplied the unread-count fetches. socket.io auto-reconnects an
        // existing socket; disconnect() nulls it so a real reconnect still works.
        if (this.socket) {
            return;
        }
        
        // REACT_APP_API_URL includes the /api path (e.g. http://localhost:5000/api).
        // Socket.io-client interprets any path component as the namespace, so
        // connecting to "http://host/api" tries to join namespace "/api" which
        // the server doesn't define — causing the "Invalid namespace" error.
        // Strip everything from /api onward to get the bare server origin.
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const socketUrl = apiUrl.replace(/\/api(\/.*)?$/, '');
        
        // Server-side handshake auth: web sends the httpOnly cookie
        // (withCredentials), native sends the stored Bearer token. Without
        // one of these the server refuses join-user-room.
        const nativeToken = getToken();
        this.socket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            withCredentials: true,
            ...(nativeToken ? { auth: { token: nativeToken } } : {})
        });
        
        this.socket.on('connect', () => {
            console.log('Socket.io connected:', this.socket.id);
            this.connected = true;

            // Subscribe to web push notifications (non-blocking)
            this.subscribeToPush().catch(() => {});

            // Join user-specific room
            this.socket.emit('join-user-room', userId);
            
            // If user is a verifier, join verifier rooms
            if (userRole === 'verifier') {
                this.socket.emit('join-verifier-room', userId);
            }
        });
        
        this.socket.on('disconnect', () => {
            console.log('Socket.io disconnected');
            this.connected = false;
        });
        
        this.socket.on('connect_error', (error) => {
            console.error('Socket.io connection error:', error);
        });
        
        // Setup event listeners
        this.setupEventListeners();
    }
    
    /**
     * Setup event listeners for notifications
     */
    setupEventListeners() {
        if (!this.socket) return;
        
        // General notification
        this.socket.on('notification', (data) => {
            console.log('Notification received:', data);
            this.notifyListeners('notification', data);
            this.showBrowserNotification(data.title, data.message);
        });
        
        // New verification request (for all verifiers)
        this.socket.on('new-verification-request', (data) => {
            console.log('New verification request:', data);
            this.notifyListeners('new-verification-request', data);
            this.showBrowserNotification(
                'New Verification Request',
                `New verification opportunity in ${data.location || 'your area'}`
            );
        });
        
        // Verification assigned to specific verifier
        this.socket.on('verification-assigned', (data) => {
            console.log('Verification assigned:', data);
            this.notifyListeners('verification-assigned', data);
            this.showBrowserNotification(
                'Verification Assigned',
                `You have been assigned to verify: ${data.milestoneTitle || 'a project'}`
            );
        });
        
        // Project updates
        this.socket.on('project-updated', (data) => {
            console.log('Project updated:', data);
            this.notifyListeners('project-updated', data);
        });

        // BOQ / materials schedule live updates (emitted to the project room)
        ['boq-created', 'boq-updated', 'boq-acknowledged', 'boq-change',
         'boq-purchase', 'boq-labor-payment', 'boq-comment'].forEach((event) => {
            this.socket.on(event, (data) => {
                this.notifyListeners(event, data);
            });
        });

        // Daily diary updates
        this.socket.on('diary-entry-created', (data) => {
            console.log('Diary entry created:', data);
            this.notifyListeners('diary-entry-created', data);
        });

        this.socket.on('diary-entry-updated', (data) => {
            console.log('Diary entry updated:', data);
            this.notifyListeners('diary-entry-updated', data);
        });

        this.socket.on('diary-entry-deleted', (data) => {
            console.log('Diary entry deleted:', data);
            this.notifyListeners('diary-entry-deleted', data);
        });
        
        // Milestone updates
        this.socket.on('milestone-updated', (data) => {
            console.log('Milestone updated:', data);
            this.notifyListeners('milestone-updated', data);
        });
        
        // Merchant quote request notifications
        this.socket.on('merchant-quote-request', (data) => {
            console.log('New quote request received:', data);
            this.notifyListeners('merchant-quote-request', data);
            this.showBrowserNotification(
                'New Material Quote Request',
                `You have received a new quote request for project: ${data.projectTitle || 'N/A'}`
            );
        });
        
        // Client quote received notifications
        this.socket.on('quote-received', (data) => {
            console.log('New quote received:', data);
            this.notifyListeners('quote-received', data);
            this.showBrowserNotification(
                'New Quote Received',
                `${data.merchantName || 'A supplier'} has submitted a quote for your request`
            );
        });
        
        // Order notifications
        this.socket.on('order-placed', (data) => {
            console.log('Order placed:', data);
            this.notifyListeners('order-placed', data);
            if (data.merchantId) {
                this.showBrowserNotification(
                    'New Order Received',
                    `You have received a new order from ${data.clientName || 'a client'}`
                );
            }
        });
        
        // Delivery notifications
        this.socket.on('delivery-submitted', (data) => {
            console.log('Delivery submitted:', data);
            this.notifyListeners('delivery-submitted', data);
            const deliveryType = data.deliveryType || (data.deliveryMethod === 'pickup' ? 'picked up' : 'delivered');
            const title = data.title || (deliveryType === 'picked up' ? 'Order Picked Up' : 'Order Delivered');
            const message = data.message || `Your materials have been ${deliveryType}. Please confirm receipt within 72 business hours.`;
            this.showBrowserNotification(
                title,
                message
            );
        });
        
        // Payment release notifications
        this.socket.on('payment-released', (data) => {
            console.log('Payment released:', data);
            this.notifyListeners('payment-released', data);
            this.showBrowserNotification(
                'Payment Released',
                `Payment of $${data.amount || 'N/A'} has been released to your account`
            );
        });
    }
    
    /**
     * Show browser notification
     */
    async showBrowserNotification(title, body) {
        if (!('Notification' in window)) {
            return;
        }
        
        if (Notification.permission === 'granted') {
            new Notification(title, {
                body,
                icon: '/logo192.png',
                badge: '/logo192.png'
            });
        } else if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                new Notification(title, {
                    body,
                    icon: '/logo192.png',
                    badge: '/logo192.png'
                });
            }
        }
    }
    
    /**
     * Request notification permission
     */
    async requestNotificationPermission() {
        if (!('Notification' in window)) {
            console.log('This browser does not support notifications');
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }

        return false;
    }

    /**
     * Subscribe to web push notifications (VAPID-based).
     * Call after login when notification permission is granted.
     */
    async subscribeToPush() {
        try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                console.log('[Push] Browser does not support push notifications');
                return;
            }

            const permission = await this.requestNotificationPermission();
            if (!permission) return;

            // Get VAPID key from backend
            const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
            if (!vapidKey) {
                console.log('[Push] VAPID key not configured');
                return;
            }

            const registration = await navigator.serviceWorker.ready;
            const existing = await registration.pushManager.getSubscription();

            let subscription = existing;
            if (!subscription) {
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: this._urlBase64ToUint8Array(vapidKey)
                });
            }

            // Send subscription to backend
            await axios.post(`${API_BASE_URL}/push/subscribe`, {
                subscription: subscription.toJSON()
            }, { withCredentials: true });

            console.log('[Push] Subscribed to web push');
        } catch (err) {
            console.warn('[Push] Failed to subscribe:', err.message);
        }
    }

    /**
     * Unsubscribe from web push (call on logout).
     */
    async unsubscribeFromPush() {
        try {
            if (!('serviceWorker' in navigator)) return;
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await axios.delete(`${API_BASE_URL}/push/unsubscribe`, {
                    data: { endpoint: subscription.endpoint },
                    withCredentials: true
                });
                await subscription.unsubscribe();
            }
        } catch (err) {
            console.warn('[Push] Failed to unsubscribe:', err.message);
        }
    }

    _urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
    
    /**
     * Add event listener
     */
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
        
        // Return unsubscribe function
        return () => {
            const listeners = this.listeners.get(event);
            if (listeners) {
                const index = listeners.indexOf(callback);
                if (index > -1) {
                    listeners.splice(index, 1);
                }
            }
        };
    }
    
    /**
     * Notify all listeners for an event
     */
    notifyListeners(event, data) {
        const listeners = this.listeners.get(event);
        if (listeners) {
            listeners.forEach(callback => callback(data));
        }
    }
    
    /**
     * Join a project room
     */
    joinProject(projectId) {
        if (this.socket && this.connected) {
            this.socket.emit('join-project', projectId);
        }
    }
    
    /**
     * Send project update
     */
    sendProjectUpdate(projectId, data) {
        if (this.socket && this.connected) {
            this.socket.emit('project-update', { projectId, ...data });
        }
    }
    
    /**
     * Send milestone update
     */
    sendMilestoneUpdate(projectId, data) {
        if (this.socket && this.connected) {
            this.socket.emit('milestone-update', { projectId, ...data });
        }
    }
    
    /**
     * Notify verification request created
     */
    notifyVerificationRequestCreated(data) {
        if (this.socket && this.connected) {
            this.socket.emit('verification-request-created', data);
        }
    }
    
    /**
     * Notify verifier assigned
     */
    notifyVerifierAssigned(data) {
        if (this.socket && this.connected) {
            this.socket.emit('verifier-assigned', data);
        }
    }
    
    /**
     * Disconnect socket
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.connected = false;
            this.listeners.clear();
        }
    }
    
    /**
     * Check if connected
     */
    isConnected() {
        return this.connected;
    }

    // ==================== REST API METHODS ====================

    /**
     * Get request config — relies on httpOnly cookie (withCredentials is set
     * globally by AuthContext) so no Authorization header is constructed here.
     * Previously this read localStorage.getItem('token') which is always null,
     * causing the backend to receive "Bearer null" and reject with AUTH_TOKEN_INVALID
     * before it could fall through to the cookie check.
     */
    getAuthHeaders() {
        return {
            headers: {
                'Content-Type': 'application/json'
            }
        };
    }

    /**
     * Get all notifications for current user
     * @param {Object} options - Query options {limit, offset, unreadOnly, type, category}
     * @returns {Promise<Object>} Notifications and metadata
     */
    async getNotifications(options = {}) {
        try {
            const params = new URLSearchParams();
            if (options.limit) params.append('limit', options.limit);
            if (options.offset) params.append('offset', options.offset);
            if (options.unreadOnly) params.append('unreadOnly', 'true');
            if (options.type) params.append('type', options.type);
            if (options.category) params.append('category', options.category);
            if (options.includeArchived) params.append('includeArchived', 'true');

            const response = await axios.get(
                `${API_BASE_URL}/notifications?${params.toString()}`,
                this.getAuthHeaders()
            );

            return {
                notifications: response.data.data || [],
                pagination: response.data.pagination || {},
                unreadCount: response.data.unreadCount || 0
            };
        } catch (error) {
            console.error('Failed to get notifications:', error);
            throw error;
        }
    }

    /**
     * Get unread notification count
     * @returns {Promise<Number>} Unread count
     */
    async getUnreadCount() {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/notifications/unread-count`,
                this.getAuthHeaders()
            );
            return response.data.data.count || 0;
        } catch (error) {
            console.error('Failed to get unread count:', error);
            throw error;
        }
    }

    /**
     * Get a single notification by ID
     * @param {Number} notificationId - Notification ID
     * @returns {Promise<Object>} Notification
     */
    async getNotificationById(notificationId) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/notifications/${notificationId}`,
                this.getAuthHeaders()
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to get notification:', error);
            throw error;
        }
    }

    /**
     * Mark notification as read
     * @param {Number} notificationId - Notification ID
     * @returns {Promise<Object>} Updated notification
     */
    async markAsRead(notificationId) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/notifications/${notificationId}/read`,
                {},
                this.getAuthHeaders()
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to mark as read:', error);
            throw error;
        }
    }

    /**
     * Mark all notifications as read
     * @returns {Promise<Number>} Number of notifications marked as read
     */
    async markAllAsRead() {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/notifications/read-all`,
                {},
                this.getAuthHeaders()
            );
            return response.data.data.count || 0;
        } catch (error) {
            console.error('Failed to mark all as read:', error);
            throw error;
        }
    }

    /**
     * Archive a notification
     * @param {Number} notificationId - Notification ID
     * @returns {Promise<Object>} Updated notification
     */
    async archiveNotification(notificationId) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/notifications/${notificationId}/archive`,
                {},
                this.getAuthHeaders()
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to archive notification:', error);
            throw error;
        }
    }

    /**
     * Delete a notification
     * @param {Number} notificationId - Notification ID
     * @returns {Promise<Boolean>} Success status
     */
    async deleteNotification(notificationId) {
        try {
            await axios.delete(
                `${API_BASE_URL}/notifications/${notificationId}`,
                this.getAuthHeaders()
            );
            return true;
        } catch (error) {
            console.error('Failed to delete notification:', error);
            throw error;
        }
    }

    /**
     * Get notification statistics
     * @returns {Promise<Object>} Statistics
     */
    async getStats() {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/notifications/stats`,
                this.getAuthHeaders()
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to get notification stats:', error);
            throw error;
        }
    }

    /**
     * Get notification preferences
     * @returns {Promise<Object>} Preferences
     */
    async getPreferences() {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/notifications/preferences/me`,
                this.getAuthHeaders()
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to get notification preferences:', error);
            throw error;
        }
    }

    /**
     * Update notification preferences
     * @param {Object} preferences - Updated preferences
     * @returns {Promise<Object>} Updated preferences
     */
    async updatePreferences(preferences) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/notifications/preferences/me`,
                preferences,
                this.getAuthHeaders()
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to update notification preferences:', error);
            throw error;
        }
    }

    /**
     * Create a notification (admin/system use)
     * @param {Object} notificationData - Notification data
     * @returns {Promise<Object>} Created notification
     */
    async createNotification(notificationData) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/notifications`,
                notificationData,
                this.getAuthHeaders()
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to create notification:', error);
            throw error;
        }
    }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
